// src/services/documents.js
//
// 🟢 DOCUMENTS (web) — send + first-page thumbnails, matching the phone exactly.
//
// Contract notes that bite:
//   • The file and its THUMBNAIL are TWO separate encrypted uploads, each with its own
//     symmetric key sealed under its own field (`documentPerDeviceKeys` /
//     `documentThumbPerDeviceKeys`) and its own `senderDeviceId` — and the thumb's id
//     field is `documentThumbSenderDeviceId`, NOT the shared one.
//   • `senderDeviceId` is TOP-LEVEL and must accompany `documentPerDeviceKeys`;
//     without it `unsealDocumentKey` returns null and the file is undecryptable.
//   • `documentSize` must be a NUMBER of bytes — a string makes the phone render
//     "0 B / Unknown size" under a live upload.
//   • Documents have NO caption anywhere in the phone's pipeline. Don't invent one.
//   • The file-type icon and the viewer both key off the EXTENSION IN `documentName`,
//     never off `documentType` — so the name must keep its extension.
//   • Several documents = several standalone messages. There is no album equivalent
//     (ChatModal ~8874 loops sendDocumentMessage one at a time), so the web does too.

import {
  collection, doc, serverTimestamp,
} from 'firebase/firestore';
import { db } from '../config/firebase';
import { sealForUsers } from '../crypto/e2ee';
import { relayNewMessage } from './socket';
import { uploadEncryptedBytes, writeChatDoc, newTempId } from './mediaUpload';
import { readFileBytes } from './mediaTools';
import { sealMembers } from './groupMembers';
import { setMessageDoc } from './sendDoc'; // 🖥📱 origin + my other devices' copy

export const MAX_DOC_BYTES = 100 * 1024 * 1024;

/** The phone's palette, keyed off the extension exactly like getDocumentIcon
 *  (MessageBubble ~4728) so a file looks the same on both clients. */
export function docStyle(name = '') {
  const ext = String(name).split('.').pop()?.toLowerCase() || '';
  const map = {
    pdf: '#E53935',
    doc: '#1565C0', docx: '#1565C0', rtf: '#1565C0', txt: '#546E7A',
    xls: '#2E7D32', xlsx: '#2E7D32', csv: '#2E7D32', numbers: '#2E7D32',
    ppt: '#E64A19', pptx: '#E64A19', key: '#E64A19',
    zip: '#6D4C41', rar: '#6D4C41', '7z': '#6D4C41', tar: '#6D4C41', gz: '#6D4C41',
  };
  return { ext: ext.toUpperCase(), color: map[ext] || '#607D8B' };
}

export function humanBytes(n) {
  const b = Number(n);
  if (!Number.isFinite(b) || b <= 0) return '';
  if (b < 1024) return `${b} B`;
  if (b < 1024 * 1024) return `${Math.round(b / 1024)} KB`;
  return `${(b / (1024 * 1024)).toFixed(1)} MB`;
}

export function isDocFile(f) {
  if (!f) return false;
  if (f.type?.startsWith('image/') || f.type?.startsWith('video/')) return false;
  return true; // anything that isn't a photo or a clip travels as a document
}

// ── first-page thumbnail ───────────────────────────────────────────────────
//
// 🟢 pdf.js is loaded LAZILY FROM A CDN rather than bundled. The project has no PDF
//    dependency, and a bundled one would add ~1MB to every page load for a feature used
//    when sending the occasional PDF. Everything here fails soft: no network, blocked
//    CDN, or a non-PDF file simply means no thumbnail — which the phone already treats
//    as normal (its own Android generator only handles PDFs, and every failure falls
//    back to the plain icon card).
//    To vendor it instead: `npm i pdfjs-dist` and swap loadPdfJs() for an import.
const PDFJS_URL = 'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/4.6.82/pdf.min.mjs';
const PDFJS_WORKER = 'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/4.6.82/pdf.worker.min.mjs';
let _pdfjs = null;

async function loadPdfJs() {
  if (_pdfjs) return _pdfjs;
  try {
    const mod = await import(/* webpackIgnore: true */ PDFJS_URL);
    mod.GlobalWorkerOptions.workerSrc = PDFJS_WORKER;
    _pdfjs = mod;
    return mod;
  } catch {
    return null;
  }
}

/** PNG bytes of page 1, or null. */
export async function pdfFirstPageThumb(file, { maxWidth = 420 } = {}) {
  const isPdf = file.type === 'application/pdf' || /\.pdf$/i.test(file.name || '');
  if (!isPdf) return null;
  try {
    const pdfjs = await loadPdfJs();
    if (!pdfjs) return null;
    const data = await file.arrayBuffer();
    const pdf = await pdfjs.getDocument({ data }).promise;
    const page = await pdf.getPage(1);
    const base = page.getViewport({ scale: 1 });
    const scale = Math.min(2, maxWidth / base.width);
    const viewport = page.getViewport({ scale });
    const canvas = document.createElement('canvas');
    canvas.width = Math.round(viewport.width);
    canvas.height = Math.round(viewport.height);
    const ctx = canvas.getContext('2d');
    ctx.fillStyle = '#fff';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    await page.render({ canvasContext: ctx, viewport }).promise;
    const blob = await new Promise((res) => canvas.toBlob(res, 'image/png'));
    if (!blob) return null;
    return {
      bytes: new Uint8Array(await blob.arrayBuffer()),
      width: canvas.width,
      height: canvas.height,
    };
  } catch {
    return null;
  }
}

// ── send ───────────────────────────────────────────────────────────────────
export async function sendDocumentMessage({ chat, myUid, myName, file, onProgress, reply, tempId, registerAbort }) {
  if (!chat?.id || !file) throw new Error('nothing to send');
  if (file.size > MAX_DOC_BYTES) throw new Error(`"${file.name}" is too large (max 100 MB).`);

  const participants = sealMembers(chat);   // 🔐 active members only
  const isGroup = !!chat.isGroup;
  const clientTempId = tempId || newTempId();

  // the file itself carries the progress; the thumbnail is a rounding error beside it
  const bytes = await readFileBytes(file);
  const up = await uploadEncryptedBytes(bytes, (f) => onProgress?.(f * 0.94), registerAbort);

  const sealed = await sealForUsers(JSON.stringify({ k: up.k, n: up.n }), participants);
  if (!sealed || !Object.keys(sealed.perDeviceKeys || {}).length) {
    throw new Error("Couldn't secure the file — it was not sent.");
  }

  // optional first page — never allowed to fail the send
  let thumbFields = {};
  try {
    const thumb = await pdfFirstPageThumb(file);
    if (thumb?.bytes?.length) {
      const tUp = await uploadEncryptedBytes(thumb.bytes);
      const tSealed = await sealForUsers(JSON.stringify({ k: tUp.k, n: tUp.n }), participants);
      if (tSealed && Object.keys(tSealed.perDeviceKeys || {}).length) {
        thumbFields = {
          documentThumbUrl: tUp.url,
          documentThumbPerDeviceKeys: tSealed.perDeviceKeys,
          documentThumbSenderDeviceId: tSealed.senderDeviceId, // its OWN id field
          documentThumbWidth: thumb.width,
          documentThumbHeight: thumb.height,
        };
      }
    }
  } catch { /* icon card is a perfectly good document bubble */ }
  onProgress?.(0.99);

  const msgRef = doc(collection(db, 'chats', chat.id, 'messages'));
  const messageId = msgRef.id;
  const preview = `Document: ${file.name}`;

  await setMessageDoc(msgRef, {
    type: 'document',
    clientTempId,
    documentUrl: up.url,
    documentPerDeviceKeys: sealed.perDeviceKeys, e2eeMedia: true,
    senderDeviceId: sealed.senderDeviceId,
    ...thumbFields,
    documentName: file.name,
    documentSize: Number(file.size) || 0,     // NUMBER of bytes
    documentType: file.type || '',
    senderId: myUid,
    timestamp: serverTimestamp(),
    sent: true,
    delivered: false,
    seen: false,
    ...(isGroup ? { senderName: myName || 'Web', isGroup: true } : {}),
    // the sealed quote — an object in Firestore, an object on the wire (the server
    // re-stringifies replyTo itself); the two *PerDeviceKeys maps must stay objects
    ...(reply || {}),
  });

  relayNewMessage({
    chatId: chat.id, messageId, fromUserId: myUid, participants, isGroup,
    cloudText: preview, clientMsgId: clientTempId,
    extra: {
      type: 'document',
      documentUrl: up.url,
      documentName: file.name,
      documentSize: Number(file.size) || 0,
      documentType: file.type || '',
      documentPerDeviceKeys: sealed.perDeviceKeys, e2eeMedia: true,
      senderDeviceId: sealed.senderDeviceId,
      documentThumbUrl: thumbFields.documentThumbUrl || null,
      documentThumbPerDeviceKeys: thumbFields.documentThumbPerDeviceKeys || null,
      documentThumbSenderDeviceId: thumbFields.documentThumbSenderDeviceId || null,
      documentThumbWidth: thumbFields.documentThumbWidth || 0,
      documentThumbHeight: thumbFields.documentThumbHeight || 0,
      ...(isGroup ? { senderName: myName || 'Web' } : {}),
      ...(reply || {}),
    },
  });

  await writeChatDoc({ chat, myUid, myName, messageId, preview });
  onProgress?.(1);
  return messageId;
}
